import React, { useEffect, useState } from 'react';
import { CTA_TYPE, CTA_SIZE, COMPONENT_MODES, ComponentModeType } from '../../../data/constants';
import CtaComponent from '../../../components/reusable-ui/cta';
import CustomDropdown, { CustomDropdownCountType } from '../../../components/reusable-ui/custom-dropdown';
import _, { cloneDeep } from 'lodash';
import CustomTextField from '../../../components/reusable-ui/textfield-box';
import { useSelector } from 'react-redux';
import {
  convertArrayToObject,
  isEditMode,
  isViewMode,
} from '../../../helpers/catalog-helper';
import { allowEverything } from '../../../utils';
import { useDispatch } from 'react-redux';
import { fetchCommonData } from '../../../redux/slices';
import { METHODS } from '../../../data/api-method-constants';
import { ENDPOINT } from '../../../services/apiUrls';
import { toastSuccess } from '../../../utils/react-toaster';
import { isFeatureAllowed, removeKeys } from '../../../helpers/common-helper';
import { transformDataFromMapToArr } from '.';
import { XML_REPORT_TYPES_LIST } from '../../../data/accounting-constants';
import { ALL_FEATURE_TYPES_MAP } from '../../../data/user-features-constants';
import { INITIAL_ROW_LABEL_INFO, MASTER_CONFIG_COLS_CONFIG } from './xml-constants';

const MASTER_KEY = 'branchId';

interface LabelType {
  // Sales Report
  'Sales Cost Center'?: string;
  'Sales Voucher Name'?: string;
  'Settlement Cost Center'?: string;
  'Settlement Voucher Name'?: string;
  // Purchase Report
  'Purchase Voucher Name'?: string;
  'Purchase Cost Center'?: string;
}

export interface ConfigRow {
  [MASTER_KEY]?: any;
  labelInfo?: LabelType;
  mid?: string;
  version?: number;
  isEdited?: boolean;
}

interface StateType {
  configBy: string;
  configRows: ConfigRow[];
  selectedBranchIds: string[];
}

type Props = {
  configRows: any[];
  mode: string;
  setMode: (val: ComponentModeType) => void;
  getMasterConfig: () => void;
  selectedReportType: string;
}

const MasterConfig = (props: Props) => {
  const {configRows, mode, setMode, getMasterConfig, selectedReportType} = props;
  const isSalesReport = selectedReportType === XML_REPORT_TYPES_LIST[0].value;
  const [state, setState] = useState<StateType>({
    configBy: 'Branch',
    configRows,
    selectedBranchIds: [],
  });
  const dispatch = useDispatch();
  const TABLE_COLS = [
    { label: state.configBy, width: '22%', key: MASTER_KEY },
    ...MASTER_CONFIG_COLS_CONFIG[selectedReportType],
    { label: '', width: '48px', key: 'delete' },
  ];
  const rowInitialState = {
    [MASTER_KEY]: [],
    labelInfo: INITIAL_ROW_LABEL_INFO[selectedReportType],
    isEdited: false,
  };

  const { permittedBranchesData, permittedInventoryBranchesData } = useSelector(
    (state: any) => ({
      permittedBranchesData: convertArrayToObject(
        state?.businessReducer?.userData?.permittedBranches || [],
        "branchId"
      ),
      permittedInventoryBranchesData: convertArrayToObject(
        state.businessReducer.permittedInventoryBranches || [],
        "mid"
      ),
    })
  );

  useEffect(() => {
    if (state?.configRows?.length === 0) {
      addRow();
      setMode(COMPONENT_MODES.editMode);
    }
  }, [selectedReportType, state?.configRows?.length]);

  useEffect(() => {
    setState({ ...state, configRows });
  }, [configRows]);

  function getMasterDropdownConfigs(rowValue: any, rowIndex: any, key: any) {
    let filteredValues: string[] = [];
    state.configRows.forEach((thisValue: any, index: any) => {
      if (index !== rowIndex)
        filteredValues = filteredValues.concat(thisValue.branchId);
    });
    let branchDataMap =
      isSalesReport
        ? permittedBranchesData
        : permittedInventoryBranchesData;
    let updatedPermittedBranches = removeKeys(
      branchDataMap,
      filteredValues
    );
    return {
      addEmptyOption: true,
      dropdownHeading: 'Branches',
      placeholder: `Select ${state.configBy}`,
      isMultiSelect: rowValue.mid ? false : true,
      optionsMap: updatedPermittedBranches,
      onSubmit: (value: any) => handleRowUpdate(rowIndex, key, value, true),
      uniqueKey: isSalesReport ? 'branchId': 'mid',
      selectedTextClass : 'text-black font-500 text-16',
      preSelectedValues: rowValue.mid ? rowValue[key][0] : rowValue[key],
      showErrorBox: false,
      isDisabled: isViewMode(mode) || rowValue.mid ,
      showCountType: CustomDropdownCountType.onePlus,
      countSuffix: 'Branches',
      getListItemText: (val: any) => val?.branchName || '',
    };
  }

  function toggleEdit() {
    if (isEditMode(mode)) {
      saveMasterConfig();
    } else {
      let newMode = COMPONENT_MODES.editMode;
      setMode(newMode);
    }
  }

  /*
    takes configRows which may contain many rows with 
    multiple branches data , and flattens them all
  */
  function getBranchWiseData() {
    let branchWiseData: { branchId: any; labelInfo: any }[] = [];
    state.configRows.forEach((row: any) => {
      row[MASTER_KEY].forEach((branchId: any) => {
        let rowData = Object.assign({}, row);
        delete rowData[MASTER_KEY];
        branchWiseData.push({
          branchId,
          ...rowData,
        });
      });
    });
    return branchWiseData;
  }

  // returns true if any value has missing properties.
  function validateConfig(branchWiseData: any) {
    if (branchWiseData.length === 0) return true;
    return branchWiseData.some((branchData: any) => {
      return Object.values(branchData.labelInfo).some(
        (label: any) => label === ''
      );
    });
  }

  function saveMasterConfig() {
    let branchWiseData = getBranchWiseData();
    let filteredDataBranchData = branchWiseData.filter(
      (value: any) => value.isEdited
    );
    if (filteredDataBranchData.length === 0) {
      saveCallback({ res: 'success' });
      return;
    }
    let branchDataListNew = filteredDataBranchData.map((data: any) => {
      let newData = {
        ...data,
        labelInfo: transformDataFromMapToArr(data.labelInfo),
      };
      return newData;
    });
    let payload = {
      info: [...branchDataListNew],
      isNewCode: true,
      reportType: selectedReportType
    };
    dispatch(
      fetchCommonData({
        method: METHODS.POST,
        body: payload,
        url: ENDPOINT.ACCOUNTING.masterConfig(selectedReportType),
        callback: saveCallback,
      })
    );
  }

  function saveCallback(response: any) {
    if (!response.code) {
      toastSuccess('Mapping Updated');
      let newMode = COMPONENT_MODES.viewMode;
      setMode(newMode);
      getMasterConfig();
    } else {
      // ToastNotifyError(getErrorMessage(pos_errormap_without_label, response.code));
    }
  }

  function addRow() {
    let newConfigRows = [...state.configRows, rowInitialState];
    setState({ ...state, configRows: newConfigRows });
  }

  function handleRowUpdate(
    index: any,
    key: any,
    value: any,
    isDropdown: boolean
  ) {
    let oldValue = cloneDeep(state.configRows[index]);
    let newVal: ConfigRow;
    if (isDropdown) {
      newVal = { ...oldValue, branchId: value };
    } else {
      let labelInfo = { ...oldValue.labelInfo, [key]: value };
      newVal = { ...oldValue, labelInfo, isEdited: true };
    }
    let newRows = state.configRows.map((rowValue: any, rowIndex: any) => {
      if (rowIndex !== index) {
        return rowValue;
      } else {
        return newVal;
      }
    });
    setState({ ...state, configRows: newRows });
  }

  const handleDeleteRow = (itemIndex: any) => {
    setState((state: any) => {
      const updatedRows = [...state.configRows];
      updatedRows.splice(itemIndex, 1);
      return { ...state, configRows: updatedRows };
    });
  };

  function renderDeleteIcon(rowIndex: any) {
    return (
      isEditMode(mode) && (
        <div
          onClick={() => handleDeleteRow(rowIndex)}
          className="my-auto p-7  border-[0.5px] border-[#397EF5] rounded-[6px] bg-[#2A77F2]/10"
        >
          <div className="rista-sprite ic-delete-blue "></div>
        </div>
      )
    );
  }

  function renderInputValue(rowIndex: any, headerValue: any, rowValue: any) {
    return isEditMode(mode) ? (
      <CustomTextField
        parentClass="w-full"
        placeholder="Enter value"
        regexCheck={allowEverything}
        value={rowValue[headerValue.key]}
        maxLength={50}
        showErrorBox={false}
        customClass="pl-17"
        isDisabled={isViewMode(mode)}
        handleChange={(value: any) => {
          handleRowUpdate(rowIndex, headerValue.key, value, false);
        }}
      />
    ) : (
      <div className="">{rowValue[headerValue.key]}</div>
    );
  }

  function renderConfigHeader() {
    return (
      <div className="flex w-full bg-[#EDEDED] ">
        {TABLE_COLS.map((value: any, index: any) => {
          return (
            <div
              style={{ width: value.width }}
              className={` ${
                value?.key !== 'delete' ? 'border-black/[0.1] border-r-[1px]' : 'bg-white '
              } text-black font-medium text-14 flex py-16 `}
              key={index}
            >
              <div
                className={`${value?.key === MASTER_KEY ? 'pl-16' : 'mx-auto'}`}
              >
                {value.label}
              </div>
            </div>
          );
        })}
      </div>
    );
  }

  function getBranchName(branchId: any) {
    let branchDataMap = isSalesReport
      ? permittedBranchesData
      : permittedInventoryBranchesData;
    return branchDataMap[branchId]?.branchName;
  }

  function renderConfigRows() {
    return (
      <div className="flex w-full flex-col">
        {state.configRows.map((rowValue: any, rowIndex: any) => {
          let { labelInfo } = rowValue;
          return (
            <div className="flex" key={rowIndex}>
              {TABLE_COLS.map((headerValue: any, index: any) => {
                return (
                  <div
                    className={`p-16 h-80 flex items-center ${
                      headerValue?.key === MASTER_KEY ? 'bg-[#EAF1FE]' : ''
                    } ${
                      headerValue?.key !== 'delete'
                        ? 'border-black/[0.1] border-r-[1px] border-b-[1px]'
                        : ''
                    }
                    `}
                    style={{ width: headerValue.width }}
                  >
                    {headerValue.key === MASTER_KEY ? (
                      !rowValue.mid ? (
                        <CustomDropdown
                          {...getMasterDropdownConfigs(
                            rowValue,
                            rowIndex,
                            headerValue?.key
                          )}
                        />
                      ) : (
                        <div className="font-medium text-black text-16">
                          {getBranchName(rowValue.branchId[0])}
                        </div>
                      )
                    ) : headerValue.key === 'delete' ? (
                      !rowValue.mid && renderDeleteIcon(rowIndex)
                    ) : (
                      renderInputValue(rowIndex, headerValue, labelInfo)
                    )}
                  </div>
                );
              })}
            </div>
          );
        })}
      </div>
    );
  }

  function renderAddBranchButton() {
    return (
      isEditMode(mode) && (
        <div
          className="flex pr-45 pt-15 cursor-pointer text-[#2A77F2] font-medium text-16 mt-10 pl-[16px]"
          onClick={addRow}
        >
          +Add More Branches
        </div>
      )
      /* { isViewMode(mode) && (
          <div
            onClick={props.toggleDelete}
            className="flex justify-center w-[40px] ml-auto border border-[#397EF5] rounded-[8px]"
          >
            <div className="rista-sprite my-auto ic-delete-blue "></div>
          </div>
        )} */
    );
  }

  function renderEditSaveCta() {
    return (
      <CtaComponent
        customClass={'ml-auto'}
        ctaSize={CTA_SIZE.medium}
        onCtaClick={toggleEdit}
        ctaType={
          isViewMode(mode) ? CTA_TYPE.secondary_active_cta : CTA_TYPE.active_cta
        }
        isDisabled={!isViewMode(mode) && validateConfig(getBranchWiseData())}
        ctaText={isViewMode(mode) ? 'Edit Mapping' : 'Save Mapping'}
      />
    );
  }

  return (
    <div className="pl-[63px] pr-[23px] w-full mt-24 mb-[40px]">
      <div className="flex items-center mb-18 pr-[45px]">
        <div className="font-semibold font-18 text-[#404040]/70">
          Master Configuration:
        </div>
        {isFeatureAllowed(
          ALL_FEATURE_TYPES_MAP.ALLOW_EDIT_XML_LEDGER_MAPPING
        ) && renderEditSaveCta()}
      </div>
      <div className="flex flex-col ">
        {renderConfigHeader()}
        {renderConfigRows()}
      </div>
      {renderAddBranchButton()}
    </div>
  );
};
export default MasterConfig;
